import type {
  BaseDatePickerAdapter,
  BaseDatePickerMode,
  BaseDatePickerValue,
} from "@lunagoai/vue-ui";

export type PosDateRangeValue = [string | null, string | null] | null;

function parseDateOnly(value: unknown): Date | null {
  if (value == null || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const text = String(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

function dateKey(date: Date, timeZone?: string): string {
  if (timeZone) {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date).reduce<Record<string, string>>((result, part) => {
      if (part.type !== "literal") result[part.type] = part.value;
      return result;
    }, {});
    return `${parts.year}-${parts.month}-${parts.day}`;
  }
  return [date.getFullYear(), date.getMonth() + 1, date.getDate()]
    .map((value, index) => index === 0 ? String(value).padStart(4, "0") : String(value).padStart(2, "0"))
    .join("-");
}

function zonedParts(value: Date, timeZone: string): Record<string, number> {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(value).reduce<Record<string, number>>((result, part) => {
    if (part.type !== "literal") result[part.type] = Number(part.value);
    return result;
  }, {});
}

function zonedDateTimeToUtc(date: string, timeZone: string, endOfDay: boolean): Date {
  const [year, month, day] = date.split("-").map(Number);
  const hour = endOfDay ? 23 : 0;
  const minute = endOfDay ? 59 : 0;
  const second = endOfDay ? 59 : 0;
  const millisecond = endOfDay ? 999 : 0;
  let utcMillis = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);

  // Resolve the timezone offset at the candidate instant, then repeat once
  // for DST boundaries where the first candidate crosses an offset change.
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const parts = zonedParts(new Date(utcMillis), timeZone);
    const renderedAsUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
      endOfDay ? millisecond : 0,
    );
    utcMillis += Date.UTC(year, month - 1, day, hour, minute, second, millisecond) - renderedAsUtc;
  }
  return new Date(utcMillis);
}

export function posDateBoundaryIso(value: string, timeZone?: string, endOfDay = false): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  if (!timeZone) return new Date(`${value}T${endOfDay ? "23:59:59.999" : "00:00:00"}`).toISOString();
  return zonedDateTimeToUtc(value, timeZone, endOfDay).toISOString();
}

function patternDate(date: Date, format: string, timeZone?: string): string {
  const value = dateKey(date, timeZone);
  const [year, month, day] = value.split("-");
  return format
    .replace(/yyyy/g, year)
    .replace(/MM/g, month)
    .replace(/dd/g, day)
    .replace(/M/g, String(Number(month)))
    .replace(/d/g, String(Number(day)));
}

function formatDate(date: Date, context: { locale?: string; timeZone?: string; displayFormat?: string }): string {
  if (context.displayFormat) return patternDate(date, context.displayFormat, context.timeZone);
  return new Intl.DateTimeFormat(context.locale || "en-GB", {
    dateStyle: "short",
    timeZone: context.timeZone,
  }).format(date);
}

function parseRange(value: unknown): [Date | null, Date | null] {
  if (!Array.isArray(value)) return [null, null];
  return [parseDateOnly(value[0]), parseDateOnly(value[1])];
}

export const posDateRangeAdapter: BaseDatePickerAdapter<PosDateRangeValue> = {
  parse: (value) => parseRange(value),
  serialize: (value, context) => {
    if (!Array.isArray(value) || !value[0] || !value[1]) return undefined;
    return [dateKey(value[0], context.timeZone), dateKey(value[1], context.timeZone)];
  },
  format: (value, context) => {
    if (!Array.isArray(value)) return "";
    return value.map((date) => date ? formatDate(date, context) : "")
      .join(" – ");
  },
};

export function isCompletePosDateRange(value: unknown): value is [string, string] {
  return Array.isArray(value)
    && typeof value[0] === "string"
    && typeof value[1] === "string"
    && value[0].length > 0
    && value[1].length > 0;
}

export function datePickerModeForRange(): BaseDatePickerMode {
  return "range";
}

export type PosDatePickerValue = BaseDatePickerValue;

export type PosTimeValue = string | null;

function parseTime(value: unknown): Date | null {
  if (typeof value !== "string") return null;
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  const date = new Date(1970, 0, 1);
  date.setHours(hour, minute, 0, 0);
  return date;
}

function formatTime(date: Date): string {
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

export const posTimeAdapter: BaseDatePickerAdapter<PosTimeValue> = {
  parse: (value) => parseTime(value),
  serialize: (value) => value instanceof Date ? formatTime(value) : undefined,
  format: (value) => value instanceof Date ? formatTime(value) : "",
};

export function minutesToPosTime(minutes: number): PosTimeValue {
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > 1439) return null;
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

export function posTimeToMinutes(value: PosTimeValue): number {
  const date = parseTime(value);
  return date ? date.getHours() * 60 + date.getMinutes() : 0;
}
